import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (this.#scrollToVariantMedia(newMediaGallery)) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

 /**
   * Scrolls the existing slideshow to the selected variant's media when the rendered media
   * list matches the current one, keeping the gallery order untouched. The freshly rendered
   * gallery is only swapped in when the media list itself changed (e.g. the "hide unselected
   * variant media" setting is on).
   *
   * @param {Element} newMediaGallery - The freshly rendered gallery for the selected variant.
   * @returns {boolean} Whether the gallery can stay in place (scrolled or already on target).
   */
  #scrollToVariantMedia(newMediaGallery) {
    const slideshow = this.slideshow;
    const newSlideshow = newMediaGallery.querySelector('slideshow-component');
    if (!slideshow || !newSlideshow) return false;

    const currentSlideIds = [...slideshow.querySelectorAll('slideshow-slide')].map((slide) =>
      slide.getAttribute('slide-id')
    );
    const newSlideIds = [...newSlideshow.querySelectorAll('slideshow-slide')].map((slide) =>
      slide.getAttribute('slide-id')
    );

    if (currentSlideIds.length !== newSlideIds.length) return false;

    const sameMedia = newSlideIds.every((id, index) => id === currentSlideIds[index]);
    if (!sameMedia) return false;

    const targetIndex = Number.parseInt(newSlideshow.getAttribute('initial-slide') ?? '', 10) || 0;

    if (slideshow.current !== targetIndex) {
      slideshow.select(targetIndex, undefined, { animate: true });
    }

    return true;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
